import React, { useEffect, useRef } from 'react';
import { COMMENTS_CONFIG, DOM_IDS } from '../config';

interface CommentsWidgetProps {
  slug: string;
  apiUrl?: string;
  apiKey?: string;
}

const CommentsWidget: React.FC<CommentsWidgetProps> = ({slug}) => {
  const widgetRef = useRef<HTMLDivElement>(null);
  const scriptsLoadedRef = useRef(false);

  useEffect(() => {
    const applyCommentImageAttrs = (root: HTMLElement) => {
      root.querySelectorAll('img').forEach((img) => {
        const src = `${img.getAttribute('src') || ''} ${img.getAttribute('data-src') || ''}`.toLowerCase();
        if (!img.hasAttribute('alt')) {
          if (src.includes('profile')) img.setAttribute('alt', 'User profile avatar');
          else if (src.includes('email')) img.setAttribute('alt', 'Email icon');
          else if (src.includes('pm.gif') || src.includes('/pm.')) img.setAttribute('alt', 'Private message icon');
          else if (src.includes('seoquake')) img.setAttribute('alt', 'SEOquake icon');
          else img.setAttribute('alt', '');
        }
        if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
        if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
      });
    };

    if (!document.querySelector(`link[href="${COMMENTS_CONFIG.CSS_PATH}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = COMMENTS_CONFIG.CSS_PATH;
      document.head.appendChild(link);
    }

    function initializeComments() {
      if (window.CommentsSDK && widgetRef.current) {
        window.CommentsSDK.init({
          containerId: DOM_IDS.COMMENTS_WIDGET,
          slug: slug,
          theme: document.documentElement.dataset.theme || 'light'
        });
        applyCommentImageAttrs(widgetRef.current);
      }
    }

    if (!scriptsLoadedRef.current && !document.querySelector(`script[src="${COMMENTS_CONFIG.JS_PATH}"]`)) {
      const script = document.createElement('script');
      script.src = COMMENTS_CONFIG.JS_PATH;
      script.async = true;
      script.onload = () => {
        scriptsLoadedRef.current = true;
        initializeComments();
      };
      document.body.appendChild(script);
    } else if (scriptsLoadedRef.current || window.CommentsSDK) {
      initializeComments();
    }

    const commentRoot = widgetRef.current;
    const imageObserver = commentRoot
      ? new MutationObserver(() => applyCommentImageAttrs(commentRoot))
      : null;
    if (commentRoot && imageObserver) {
      imageObserver.observe(commentRoot, { childList: true, subtree: true });
    }

    return () => {
      imageObserver?.disconnect();
      if (window.CommentsSDK && window.CommentsSDK.destroy) {
        window.CommentsSDK.destroy();
      }
    };
  }, [slug]);

  return (
    <div ref={widgetRef} id={DOM_IDS.COMMENTS_WIDGET} data-slug={slug} className="comments-section"></div>
  );
};

export default CommentsWidget;
